/**
 * Скролл внутри таблиц и списков DevExtreme на планшетах — браузеру, а не JS.
 *
 * DevExtreme решает, скроллить нативно или своей эмуляцией, по User-Agent:
 * нативный скролл считается доступным только на ios/android/mac
 * (`core/utils/support.js`, `isNativeScrollingSupported`), иначе включается эмуляция.
 *
 * На планшете такое определение врёт при любом несовпадении UA с платформой —
 * «версия для ПК» в Chrome на Android, Windows-планшеты, прошивки со своим UA. Вместо
 * нативного скролла работает эмуляция: DevExtreme сам ловит тач-жест и сам двигает
 * содержимое, а браузеру запрещает панорамирование через `touch-action`. Стоит этой
 * бухгалтерии разойтись с моментом жеста — браузер забирает жест себе (`pointercancel`),
 * эмуляция его не получает, и таблица не двигается вообще.
 *
 * Палец против мыши — это свойство ЖЕЛЕЗА, а не строки UA, и спрашивать о нём надо
 * медиавыражением. `(pointer: coarse)` истинно там, где основной ввод — палец, и не
 * зависит от подмены UA, то есть закрывает весь класс таких промахов сразу.
 *
 * Рычагов два, потому что виджеты выбирают скролл в разных местах:
 *
 * * `Scrollable` — общий скроллер. Гриды и дерево отдают ему решение целиком
 *   (`scrolling.useNative: "auto"`), поэтому одна настройка чинит все таблицы кабинета;
 * * `List` — списки (выпадающие списки, lookup) свой `useNative` задают САМИ, из
 *   собственного `useNativeScrolling`, и настройка скроллера до них не доходит.
 *
 * Ноутбуки с тачскрином не затронуты: там основной указатель — мышь, `(pointer: coarse)`
 * ложно, и остаётся привычная эмуляция с тонкими скроллбарами DevExtreme. Именно поэтому
 * условие не `any-pointer: coarse` — оно перевело бы и их, сменив привычный вид.
 */

import List from "devextreme/ui/list";
import * as scrollableModule from "devextreme/ui/scroll_view/ui.scrollable";

/** Правило `defaultOptions` виджета DevExtreme: предикат устройства и опции для него. */
interface WidgetDefaults {
  defaultOptions(rule: { device: () => boolean; options: Record<string, unknown> }): void;
}

// Класс Scrollable DevExpress пометил в типах как внутренний (@deprecated), но публичной
// ручки, которая переключила бы useNative у гридов и дерева разом, нет. Берём у модуля
// только defaultOptions через собственный узкий интерфейс — без ссылки на устаревший тип.
const Scrollable = (scrollableModule as unknown as { default: WidgetDefaults }).default;

export const COARSE_POINTER_QUERY = "(pointer: coarse)";

/** Основной указатель устройства — палец. */
export function isCoarsePointerDevice(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;

  return window.matchMedia(COARSE_POINTER_QUERY).matches;
}

export function applyDxNativeScrollOnTouch(): void {
  // предикат, а не булево: он вычисляется при создании каждого виджета, поэтому
  // подключённая мышь или смена устройства ввода учитываются без перезагрузки
  const device = () => isCoarsePointerDevice();

  Scrollable.defaultOptions({ device, options: { useNative: true } });
  List.defaultOptions({ device, options: { useNativeScrolling: true } });
}
